import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  DndContext,
  DragCancelEvent,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { ProjectsName, ProjectTitle, ProjectItems, Viewer, Icon, SortableItemWrapper } from './styles';
import { ProjectItem } from '../projectItem';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { calculateUpdatedTaskIndex } from './utils';
import { ROUTES } from '../../../routes/urls';
import { updateWorkspaceProjectPreference } from '../../../helpers/firebaseHelper';
import { ICON_SIZE } from '../../../global/constants';
import { captureException } from '../../../services/logService';
import { ProjectModel } from '../../../mobx/models/project';

interface SortableProjectItemProps {
  item: ProjectModel;
}

const FavoriteProjectDragPreview: React.FC<{ item: ProjectModel }> = ({ item }) => {
  return (
    <SortableItemWrapper isOverlay={true}>
      <ProjectItem item={item} forceHover={true} />
    </SortableItemWrapper>
  );
};

const SortableProjectItem: React.FC<SortableProjectItemProps> = ({ item }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const cssTransform = CSS.Translate.toString(transform ? { ...transform } : null) || undefined;

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <SortableItemWrapper
      ref={setNodeRef}
      isDragging={isDragging}
      cssTransform={cssTransform}
      cssTransition={transition || undefined}
      {...attributes}
      {...listeners}>
      <ProjectItem item={item} />
    </SortableItemWrapper>
  );
};

interface Props {
  isTrackingActivity: boolean;
}

const FavoriteProjects: React.FC<Props> = ({ isTrackingActivity }) => {
  const { projectStore } = rootStore;
  const { favoriteProjects } = projectStore;
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);

  const history = useHistory();
  const location = useLocation();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const sortableIds = useMemo(() => favoriteProjects.map((p) => p.id), [favoriteProjects]);

  const activeProject = useMemo(
    () => favoriteProjects.find((project) => project.id === activeProjectId) || null,
    [activeProjectId, favoriteProjects]
  );

  useEffect(() => {
    if (!activeProjectId) return undefined;

    document.body.classList.add('dragging');

    return () => {
      document.body.classList.remove('dragging');
    };
  }, [activeProjectId]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveProjectId(event.active.id as string);
  }, []);

  const handleDragCancel = useCallback((_event: DragCancelEvent) => {
    setActiveProjectId(null);
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      try {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const sourceIndex = favoriteProjects.findIndex((p) => p.id === active.id);
        const destinationIndex = favoriteProjects.findIndex((p) => p.id === over.id);
        if (sourceIndex === -1 || destinationIndex === -1) return;

        const updatedTaskIndex = calculateUpdatedTaskIndex(destinationIndex, sourceIndex, favoriteProjects);
        const draggableId = active.id as string;

        const updatedProject = favoriteProjects.find((project) => project.id === draggableId);
        if (updatedProject) {
          updatedProject.setIndex(updatedTaskIndex);
        }
        await updateWorkspaceProjectPreference(draggableId, { fav_index: updatedTaskIndex });
      } catch (error) {
        captureException(error);
      } finally {
        setActiveProjectId(null);
      }
    },
    [favoriteProjects]
  );

  if (favoriteProjects.length === 0) {
    return null;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}>
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        <ProjectsName isTrackingActivity={isTrackingActivity}>
          <ProjectTitle className='project'>Projects</ProjectTitle>
          <ProjectItems>
            {favoriteProjects.map((item) => (
              <SortableProjectItem key={item?.id} item={item} />
            ))}
            <Viewer
              role='button'
              isActive={location?.pathname === ROUTES.ALL_PROJECT}
              onClick={() => history.push(ROUTES.ALL_PROJECT)}
              className={location?.pathname === ROUTES.ALL_PROJECT ? 'active-navbar-item-menu' : 'navbar-menu-name'}>
              <Icon>
                <SVGIcon
                  name='three-dots-horizontal-icon'
                  width={ICON_SIZE.LG}
                  height={ICON_SIZE.LG}
                  viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                  fill='var(--text-primary)'
                />
              </Icon>
              All Projects
            </Viewer>
          </ProjectItems>
        </ProjectsName>
      </SortableContext>
      <DragOverlay>{activeProject ? <FavoriteProjectDragPreview item={activeProject} /> : null}</DragOverlay>
    </DndContext>
  );
};

export default observer(FavoriteProjects);
